import { makeAutoObservable, runInAction } from 'mobx';
import { toast } from 'react-toastify';
import { DIRECTION, MAX_UPLOADED_FILES_SIZE } from 'components/constants';
import i18n from 'i18n';
import {
  createNewTopicRequest,
  downloadTopicDocumentRequest,
  getTopicByIdRequest,
  getTopicMessagesRequest,
  getTopicsListRequest,
  markTopicAsReadRequest,
  sendTopicDocumentRequest,
  sendTopicMessageRequest,
  updateTopicOwnerOperatorRequest,
  updateTopicStatusRequest
} from 'services/requestAgent';
import { convertDate, convertToFullDate } from 'services/utils';

class MessagesStore {
  isInitialized = false;
  isLoading = false;
  isFileUploading = false;
  isNewMessageCreated = false;
  isTopicLoading = false;
  error = null;
  topics = [];
  currentTopicInfo = null;
  currentTopicMessages = [];
  uploadedFiles = [];
  totalElements = null;
  // Pagination for messages within a topic
  messagesPagination = {
    page: 0,
    size: 20,
    totalPages: null,
    totalElements: null,
    isFirstPage: false
  };
  isLoadingOlderMessages = false;
  sortColumn = {
    sort_column: 'last_message_sent_at',
    sort_direction: DIRECTION.DESC
  };
  pagination = {
    size: 20,
    page: 0,
    totalPages: null
  };
  filters = {
    search_text: null,
    from: null,
    to: null,
    statuses: 'ALL',
    operator: null,
    unread_only: false
  };

  constructor() {
    makeAutoObservable(this);
  }

  resetMessagesStore = () => {
    this.isInitialized = false;
    this.isLoading = false;
    this.isFileUploading = false;
    this.isNewMessageCreated = false;
    this.error = null;
    this.topics = [];
    this.currentTopicInfo = null;
    this.currentTopicMessages = [];
    this.uploadedFiles = [];
    this.totalElements = null;
    this.messagesPagination = {
      page: 0,
      size: 20,
      totalPages: null,
      totalElements: null,
      isFirstPage: false
    };
    this.isLoadingOlderMessages = false;
    this.sortColumn = {
      sort_column: 'last_message_sent_at',
      sort_direction: DIRECTION.DESC
    };
    this.pagination = {
      size: 20,
      page: 0,
      totalPages: null
    };
    this.filters = {
      search_text: null,
      from: null,
      to: null,
      statuses: 'ALL',
      operator: null,
      unread_only: false
    };
  };

  clearUploadedFiles = () => {
    this.uploadedFiles = [];
  };

  resetMessagesPagination = () => {
    this.messagesPagination = {
      page: 0,
      size: 20,
      totalPages: null,
      totalElements: null,
      isFirstPage: false
    };
  };

  clearError = () => {
    this.error = null;
  };

  setIsLoading = (status) => {
    this.isLoading = status;
    this.error = null;
  };

  setIsFileUploading = (status) => {
    this.isFileUploading = status;
  };

  setIsNewMessageCreated = (status) => {
    this.isNewMessageCreated = status;
  };

  setPageNumber = (page) => {
    this.pagination.page = page;
  };

  setPageSize = (size) => {
    this.pagination.size = size;
  };

  setFilter = (fieldName, value) => {
    this.filters[fieldName] = value;
    this.pagination.page = 0;
  };

  setSortData = (sortData) => {
    this.sortColumn.sort_column = sortData.sortBy;
    this.sortColumn.sort_direction = sortData.direction;
    // Fetch data with new sorting
    this.getTopicsList();
  };

  setFiltersFromUrl = (params) => {
    const filters = { ...this.filters };
    const pagination = { ...this.pagination };

    // Mapping URL filter parameters to corresponding properties in the 'filters' object
    const filterParamsMapping = {
      search_text: 'search_text',
      from: 'from',
      to: 'to',
      statuses: 'statuses',
      unread_only: 'unread_only'
    };

    // Iterating over each URL parameter and assigning its values to 'filters'
    for (const param in params) {
      if (param in filterParamsMapping) {
        let value = params[param];

        // Convert `from` and `to` if they exist
        if (param === 'from' || param === 'to') {
          value = convertToFullDate(value);
        }

        if (param === 'unread_only') {
          value = value !== 'false';
        }

        // Handle only array values as arrays, keep others as their original types
        if (Array.isArray(value)) {
          filters[filterParamsMapping[param]] = value;
        } else {
          filters[filterParamsMapping[param]] = value;
        }
      }
    }

    // Assigning pagination parameters from the URL
    pagination.page = parseInt(params.page) || pagination.page;
    pagination.size = parseInt(params.size) || pagination.size;

    // Assigning sorting parameters from the URL
    this.sortColumn.sort_column = params.sort_column || this.sortColumn.sort_column;
    this.sortColumn.sort_direction = params.sort_direction || this.sortColumn.sort_direction;

    // Updating the state of filters and pagination
    this.filters = filters;
    this.pagination = pagination;
  };

  prepareFiltersParams = () => {
    const params = { ...this.filters };

    if (this.filters.from) {
      params.from = convertDate(this.filters.from);
    } else {
      delete params.from;
    }

    if (this.filters.to) {
      params.to = convertDate(this.filters.to);
    } else {
      delete params.to;
    }

    // Don't send statuses parameter when ALL is selected
    if (params.statuses === 'ALL') {
      delete params.statuses;
    }

    return params;
  };

  getTopicsList = async () => {
    this.setIsLoading(true);
    try {
      const filtersParams = this.prepareFiltersParams();

      const res = await getTopicsListRequest(
        this.pagination,
        this.sortColumn.sort_column,
        this.sortColumn.sort_direction,
        filtersParams.search_text,
        filtersParams.unread_only,
        filtersParams
      );

      runInAction(() => {
        this.isInitialized = true;
        this.topics = res.content;
        this.totalElements = res.total_elements;
        this.pagination = {
          size: res.size,
          page: res.number,
          totalPages: res.total_pages
        };
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  getTopicById = async (topicId) => {
    this.isTopicLoading = true;
    try {
      let topicInfo = await getTopicByIdRequest(topicId);

      runInAction(() => {
        this.isInitialized = true;
        this.currentTopicInfo = topicInfo;

        // Add the topic to the list if it's not there
        const existingIndex = this.topics.findIndex((t) => t.id === topicId);
        if (existingIndex === -1 && topicInfo) {
          // Add the topic to the beginning of the list
          this.topics = [topicInfo, ...this.topics];
          this.setCurrentTopicIndex(0);
        } else if (existingIndex !== -1) {
          // Update existing topic in the list
          this.topics[existingIndex] = topicInfo;
          this.setCurrentTopicIndex(existingIndex);
        }
      });

      if (!topicInfo?.readByOperator) {
        await this.markTopicAsRead(topicId);
      }

      return true; // Topic exists and was loaded
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
      return false; // Topic doesn't exist or error occurred
    } finally {
      runInAction(() => {
        this.isTopicLoading = false;
      });
    }
  };

  getTopicMessages = async (topicId, loadType = 'latest') => {
    // Set appropriate loading state
    if (loadType === 'latest') {
      this.resetMessagesPagination();
      this.setIsLoading(true);
    } else if (loadType === 'previous') {
      this.isLoadingOlderMessages = true;
      this.messagesPagination.page++;
    }

    try {
      // Request with DESC sort to get newest messages first
      const res = await getTopicMessagesRequest(topicId, {
        page: this.messagesPagination.page,
        size: this.messagesPagination.size,
        sort_direction: 'DESC'
      });

      runInAction(() => {
        this.isInitialized = true;

        // Update pagination info
        this.messagesPagination = {
          page: res.number || res.page || 0,
          size: res.size || 20,
          totalPages: res.total_pages || res.totalPages || 1,
          totalElements: res.total_elements || res.totalElements || 0,
          // When using DESC, first page means we're at the oldest messages
          isFirstPage: (res.number || res.page || 0) >= (res.total_pages || res.totalPages || 1) - 1
        };

        if (loadType === 'latest') {
          // For initial load, reverse to show oldest at top, newest at bottom
          this.currentTopicMessages = res.content ? [...res.content].reverse() : [];
        } else if (loadType === 'previous') {
          // Prepend older messages to the beginning
          const olderMessages = res.content ? [...res.content].reverse() : [];
          this.currentTopicMessages = [...olderMessages, ...this.currentTopicMessages];
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      if (loadType === 'latest') {
        this.setIsLoading(false);
      } else {
        runInAction(() => {
          this.isLoadingOlderMessages = false;
        });
      }
    }
  };

  downloadTopicDocument = async (documentId) => {
    this.setIsFileUploading(true);

    const toastPromise = toast.promise(downloadTopicDocumentRequest(documentId), {
      pending: i18n.getMessage('messagesStore.downloadTopicDocument.toastPromise.pending'),
      success: i18n.getMessage('messagesStore.downloadTopicDocument.toastPromise.success')
    });

    try {
      await toastPromise;
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsFileUploading(false);
    }
  };

  markTopicAsRead = async (topicId) => {
    try {
      await markTopicAsReadRequest(topicId);

      runInAction(() => {
        // Update currentTopicInfo if it exists
        if (this.currentTopicInfo && this.currentTopicInfo.id === topicId) {
          this.currentTopicInfo = { ...this.currentTopicInfo, readByOperator: true };
        }

        // Update the topic in the topics list
        const topicIndex = this.topics.findIndex((topic) => topic.id === topicId);
        if (topicIndex !== -1) {
          this.topics[topicIndex] = {
            ...this.topics[topicIndex],
            readByOperator: true,
            read: true
          };

          // If "Unread only" filter is active, remove the read topic from the list
          if (this.filters.unread_only) {
            this.topics = this.topics.filter((topic) => topic.id !== topicId);
            // Refresh the list to get updated unread topics
            this.getTopicsList();
          }
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error marking topic as read:', err);
      // Don't set global error for background operation
    }
  };

  removeAttachment = (fileId) => {
    this.uploadedFiles = this.uploadedFiles.filter((file) => file?.id !== fileId);
  };

  uploadAttachments = async ({ target: { files } }) => {
    if (!Object.keys(files)?.length) {
      return null;
    }

    this.setIsFileUploading(true);

    try {
      const selectedFiles = [...files];
      const Data = new FormData();

      // Store file sizes for later use
      const fileSizes = {};

      selectedFiles.forEach((file) => {
        if (file.size > MAX_UPLOADED_FILES_SIZE) {
          throw { code: 'REQUEST_HAS_BEEN_TERMINATED' };
        } else {
          Data.append('file', file, file?.name);
          // Store the file size with the file name as key
          fileSizes[file.name] = file.size;
        }
      });

      const documentArray = await sendTopicDocumentRequest(Data);

      // Generate preview URLs for uploaded files
      const filesWithUrls = await Promise.all(
        documentArray.map(async (file) => {
          try {
            const url = await downloadTopicDocumentRequest(file?.id, true);
            // Determine content type based on file extension
            const extension = file?.name?.split('.').pop()?.toLowerCase();
            let contentType = 'application/octet-stream';
            if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
              contentType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
            } else if (extension === 'pdf') {
              contentType = 'application/pdf';
            }
            // Include the file size from the original file
            return {
              name: file?.name,
              id: file?.id,
              url,
              contentType,
              size: fileSizes[file?.name] || 0
            };
          } catch (err) {
            // eslint-disable-next-line no-console
            console.error(`Failed to generate preview for file ${file?.id}:`, err);
            return {
              name: file?.name,
              id: file?.id,
              size: fileSizes[file?.name] || 0
            };
          }
        })
      );

      runInAction(() => {
        this.isFileUploading = false;
        this.uploadedFiles = [...this.uploadedFiles, ...filesWithUrls];
      });
    } catch (err) {
      runInAction(() => {
        this.isFileUploading = false;
        this.error = { type: 'attachDoc', ...err };
      });
    }
  };

  createNewTopic = async (type, topicData) => {
    this.setIsLoading(true);
    try {
      const topicInfo = await createNewTopicRequest(type, topicData);

      runInAction(() => {
        this.topics = [topicInfo, ...this.topics];
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  sendTopicMessage = async (topicId, message) => {
    this.setIsLoading(true);
    try {
      // Prepare message data with documents array containing file IDs
      const messageData = {
        messageBody: message.messageBody,
        documents: message.uploadedFiles?.filter((file) => file.id).map((file) => file.id) || []
      };

      // Store original subject before any API calls
      const topicIndex = this.topics.findIndex((topic) => topic.id === topicId);
      const originalSubject = topicIndex !== -1 ? this.topics[topicIndex].subject : null;
      const originalCurrentSubject = this.currentTopicInfo?.id === topicId ? this.currentTopicInfo.subject : null;

      await sendTopicMessageRequest(topicId, messageData).then(async () => {
        await this.getTopicMessages(topicId);
        this.clearUploadedFiles();

        // Restore original subject if it was changed
        runInAction(() => {
          if (topicIndex !== -1 && originalSubject) {
            this.topics[topicIndex].subject = originalSubject;
          }
          if (this.currentTopicInfo && this.currentTopicInfo.id === topicId && originalCurrentSubject) {
            this.currentTopicInfo.subject = originalCurrentSubject;
          }
        });
      });

      runInAction(() => {
        this.isNewMessageCreated = true;
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  updateTopicOwnerOperator = async (topicId, ownerOperatorId) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(updateTopicOwnerOperatorRequest(topicId, ownerOperatorId), {
      pending: i18n.getMessage('messagesStore.updateTopicOwnerOperator.toastPromise.pending'),
      success: i18n.getMessage('messagesStore.updateTopicOwnerOperator.toastPromise.success')
    });

    try {
      const topicInfo = await toastPromise;

      runInAction(() => {
        this.currentTopicInfo = topicInfo;
        // Also update the topic in the topics array by ID, not by index
        const topicIndex = this.topics.findIndex((t) => t.id === topicId);
        if (this.topics && topicIndex !== -1) {
          this.topics[topicIndex] = {
            ...this.topics[topicIndex],
            ownerOperator: topicInfo.ownerOperator,
            readByOperator: topicInfo.readByOperator !== undefined ? topicInfo.readByOperator : true
          };
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  updateTopicStatus = async (topicId, status) => {
    this.setIsLoading(true);

    const toastPromise = toast.promise(updateTopicStatusRequest(topicId, status), {
      pending: i18n.getMessage('messagesStore.updateTopicStatus.toastPromise.pending'),
      success: i18n.getMessage('messagesStore.updateTopicStatus.toastPromise.success')
    });

    try {
      const topicInfo = await toastPromise;

      runInAction(() => {
        this.currentTopicInfo = topicInfo;
        // Find the topic by ID instead of using currentTopicIndex
        const topicIndex = this.topics.findIndex((topic) => topic.id === topicId);
        if (this.topics && topicIndex !== -1) {
          this.topics[topicIndex] = {
            ...this.topics[topicIndex],
            status: topicInfo.status,
            readByOperator: topicInfo.readByOperator !== undefined ? topicInfo.readByOperator : true
          };
        }
      });
    } catch (err) {
      runInAction(() => {
        this.error = err;
      });
    } finally {
      this.setIsLoading(false);
    }
  };

  // New methods for the redesigned interface
  hasActiveFilters = () => {
    return !!(
      this.filters.search_text ||
      this.filters.from ||
      this.filters.to ||
      (this.filters.statuses && this.filters.statuses !== 'ALL' && this.filters.statuses.length > 0) ||
      this.filters.operator ||
      this.filters.unread_only
    );
  };

  hasAnyTopics = () => {
    return this.topics && this.topics.length > 0;
  };

  // Store state for current topic
  currentTopicIndex = -1;
  isTopicMessagesLoading = false;

  setCurrentTopicIndex = (index) => {
    this.currentTopicIndex = index;
  };

  clearCurrentTopic = () => {
    this.currentTopicInfo = null;
    this.currentTopicMessages = [];
    this.currentTopicIndex = -1;
  };

  getCurrentTopicIndexById = (topicId) => {
    return this.topics.findIndex((topic) => topic.id === topicId);
  };

  // New topic creation state for FrontOffice design
  isNewTopicCreated = false;
  newTopic = {
    subject: '',
    message_body: ''
  };

  setIsNewTopicCreated = (status) => {
    this.isNewTopicCreated = status;
  };

  setNewTopicData = (subject, message) => {
    this.newTopic = { subject, message_body: message };
  };

  setPage = (page) => {
    this.pagination.page = page;
  };

  totalPages = () => {
    return this.pagination.totalPages || 0;
  };

  getCurrentTopicMessages = async (accountNumber, topicId) => {
    this.isTopicMessagesLoading = true;
    try {
      await this.getTopicMessages(topicId);
    } finally {
      this.isTopicMessagesLoading = false;
    }
  };

  markTopicAsReadByIndex = async (accountNumber, topicId, topicIndex) => {
    try {
      await markTopicAsReadRequest(topicId);
      runInAction(() => {
        if (this.topics[topicIndex]) {
          this.topics[topicIndex].read = true;
        }
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error marking topic as read:', err);
    }
  };

  // New message data for chat
  setNewMessageData = (message) => {
    this.newMessageData = message;
  };

  // File operations for compatibility
  getCurrentTopicMessagesFilesUrls = async () => {
    if (!this.currentTopicMessages) return;

    try {
      for (const message of this.currentTopicMessages) {
        if (message.documents && message.documents.length > 0) {
          for (const document of message.documents) {
            if (!document.url && document.id) {
              const blobUrl = await downloadTopicDocumentRequest(document.id, true);
              runInAction(() => {
                document.url = blobUrl;
                // Add content type based on file extension if not present
                if (!document.contentType && !document.content_type && document.name) {
                  const extension = document.name.split('.').pop()?.toLowerCase();
                  if (['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'].includes(extension)) {
                    document.contentType = `image/${extension === 'jpg' ? 'jpeg' : extension}`;
                  } else if (extension === 'pdf') {
                    document.contentType = 'application/pdf';
                  }
                }
              });
            }
          }
        }
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error loading document previews:', err);
      runInAction(() => {
        this.error = err;
      });
    }
  };

  downloadAttachment = async (accountNumber, fileId) => {
    try {
      await this.downloadTopicDocument(fileId);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error downloading attachment:', err);
    }
  };

  resetForms = () => {
    this.newTopic = { subject: '', message_body: '' };
    this.clearUploadedFiles();
  };
}

export default new MessagesStore();
